import React from 'react';
import PropTypes, { InferProps } from 'prop-types';

import classnames from 'classnames/bind';

import { GLOBAL_PERMISSIONS } from '@CONSTANTS/PERMISSIONS.constant';

import { AdminUser, useGetAllUsers } from '@SERVICES';
import useAuth from '@HOOKS/useAuth';

import BasicCheckbox from '@COMPONENTS/COMMON/inputs/checkbox/BasicCheckbox';

import styles from './Permissions.module.scss';

const cx: CX = classnames.bind(styles);

const permissions = [
    {
        id: 1,
        name: 'Can create documents',
        codename: GLOBAL_PERMISSIONS.CAN_CREATE_DOCUMENTS,
    },
    {
        id: 2,
        name: 'Can manage vendors',
        codename: GLOBAL_PERMISSIONS.CAN_MANAGE_VENDORS,
    },
    {
        id: 3,
        name: 'Can view logs',
        codename: GLOBAL_PERMISSIONS.CAN_VIEW_LOGS,
    },
    {
        id: 4,
        name: 'Can manage users',
        codename: GLOBAL_PERMISSIONS.CAN_MANAGE_PERMISSIONS,
    },
];

function Permissions(props: Props) {
    const {
        isSuperuser,
        setIsSuperuser,
        canAccessAppSettings,
        setCanAccessAppSettings,
        isSuperAdmin,
        setIsSuperAdmin,
        selectedPermissions,
        setSelectedPermissions,
        user,
    } = props;

    const { state: { user: currentUser } } = useAuth();
    const { data: usersData = { data: [] } } = useGetAllUsers();

    // Get current user's full data including isSuperAdmin and canAccessAppSettings
    const currentUserFullData = usersData.data.find((u) => u.id === currentUser?.id);

    // Determine if current user can see super admin setting (only super admins can see it)
    const canSeeSuperAdminSetting = currentUserFullData?.isSuperAdmin || false;

    // Determine if current user can see app settings access (only admins or super admins can see it)
    const canSeeAppSettingsAccess = currentUserFullData?.isSuperuser || currentUserFullData?.isSuperAdmin || false;

    // Determine if current user can edit the target user's permissions
    const canEditUserPermissions = (() => {
        // If current user is not an admin, they cannot edit any permissions
        if (!currentUserFullData?.isSuperuser && !currentUserFullData?.isSuperAdmin) {
            return false;
        }

        // If current user is admin but not super admin, they cannot edit super admin permissions
        if (currentUserFullData?.isSuperuser && !currentUserFullData?.isSuperAdmin) {
            if (user.isSuperAdmin) {
                return false;
            }
        }

        // Super admins can edit anyone's permissions
        if (currentUserFullData?.isSuperAdmin) {
            return true;
        }

        // Admins can edit regular users and other admins (but not super admins)
        if (currentUserFullData?.isSuperuser) {
            return !user.isSuperAdmin;
        }

        return false;
    })();

    return (
        <div className={cx('permissions')}>
            <div className={cx('permissions-title')}>
                Admin Permissions
            </div>

            {!canEditUserPermissions && (
                <div className={cx('permission-warning')}>
                    <strong>Permission Restricted:</strong>
                    {' '}
                    You do not have permission to edit this user&apos;s permissions.
                </div>
            )}

            <div className={cx('admin-controls')}>
                <div className={cx('is-admin')}>
                    <BasicCheckbox
                        title="Is admin"
                        checked={isSuperuser}
                        fontWeight={700}
                        disabled={!canEditUserPermissions}
                        onChange={() => {
                            setIsSuperuser((val: boolean) => !val);
                        }}
                    />
                </div>

                {canSeeAppSettingsAccess && (
                    <div className={cx('app-settings-access')}>
                        <BasicCheckbox
                            title="Can access app settings"
                            checked={canAccessAppSettings}
                            fontWeight={700}
                            disabled={!canEditUserPermissions || isSuperuser || isSuperAdmin}
                            onChange={() => {
                                setCanAccessAppSettings((val: boolean) => !val);
                            }}
                        />
                        <div className={cx('description')}>
                            Allows access to application configuration and settings
                        </div>
                    </div>
                )}

                {canSeeSuperAdminSetting && (
                    <div className={cx('super-admin')}>
                        <BasicCheckbox
                            title="Is super admin"
                            checked={isSuperAdmin}
                            fontWeight={700}
                            disabled={!canEditUserPermissions}
                            onChange={() => {
                                setIsSuperAdmin((val: boolean) => !val);
                            }}
                        />
                        <div className={cx('description')}>
                            Highest level access - can manage workspaces and system settings
                        </div>
                    </div>
                )}
            </div>

            <div className={cx('permissions-list')}>
                <div className={cx('permissions-subtitle')}>
                    System Permissions
                </div>
                {
                    permissions.map((permission) => {
                        const key = permission.id;

                        const checked = selectedPermissions.has(permission.codename);

                        const disabled = (
                            !canEditUserPermissions
                            || isSuperuser 
                            || isSuperAdmin 
                            || (permission.codename === GLOBAL_PERMISSIONS.CAN_MANAGE_PERMISSIONS && user.id === currentUser!.id)
                        );

                        return (
                            <div
                                key={key}
                                className={cx('permission-wrapper')}
                            >
                                <BasicCheckbox
                                    title={permission.name}
                                    checked={isSuperuser || isSuperAdmin || checked}
                                    disabled={disabled}
                                    onChange={() => {
                                        const set = new Set(selectedPermissions);

                                        if (checked) {
                                            set.delete(permission.codename);
                                        } else {
                                            set.add(permission.codename);
                                        }

                                        setSelectedPermissions(set);
                                    }}
                                />
                            </div>
                        );
                    })
                }
            </div>
        </div>
    );
}

Permissions.propTypes = {
    isSuperuser: PropTypes.bool.isRequired,
    setIsSuperuser: PropTypes.func.isRequired,
    canAccessAppSettings: PropTypes.bool.isRequired,
    setCanAccessAppSettings: PropTypes.func.isRequired,
    isSuperAdmin: PropTypes.bool.isRequired,
    setIsSuperAdmin: PropTypes.func.isRequired,
    selectedPermissions: PropTypes.instanceOf(Set<GLOBAL_PERMISSIONS>).isRequired,
    setSelectedPermissions: PropTypes.func.isRequired,
};

type Props = InferProps<typeof Permissions.propTypes> & {
    user: AdminUser,
};

export default Permissions;
